/**
 * AI сервис фронтенда.
 *
 * Назван geminiService по историческим причинам, но теперь все вызовы
 * проксируются на наш сервер `/api/ai/*` (который использует Claude Haiku 4.5
 * с prompt caching). Это критично для безопасности: раньше `GEMINI_API_KEY`
 * компилировался в клиентский бандл через `process.env.API_KEY`, и любой
 * гость мог вытащить его из минифицированного index.js.
 *
 * Все функции сохраняют старую сигнатуру — компоненты (AiRecommender,
 * DishDetailModal, Home.tsx) работают без изменений.
 */
import { Language, MenuItem } from '../types';

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`AI request failed: ${response.status}`);
  }
  return response.json();
}

export const recommendDish = async (
  prompt: string,
  language: Language,
): Promise<number | null> => {
  try {
    const { id } = await apiPost<{ id: number | null }>('/api/ai/recommend', {
      prompt,
      language,
    });
    return typeof id === 'number' ? id : null;
  } catch (error) {
    console.error('Error in recommendDish:', error);
    throw new Error('Failed to get recommendation from AI.');
  }
};

export const getDrinkPairing = async (
  dish: MenuItem,
  language: Language,
): Promise<string | null> => {
  // Получаем список напитков из текущего меню (нужно для фронта — но сервер
  // тоже мог бы читать из Firestore. Для совместимости сигнатуры — принимаем
  // напитки тут и передаём). Ленивый import чтобы не тянуть большой menuData
  // в bundle при каждом вызове.
  const { menuData } = await import('../data/menuData');
  const drinks =
    menuData
      .find((c) => c.id === 'drinks')
      ?.items.map((d) => d.names[language]) || [];
  if (drinks.length === 0) return null;

  try {
    const { pairing } = await apiPost<{ pairing: string | null }>(
      '/api/ai/pairing',
      {
        dishName: dish.names[language],
        dishDesc: dish.descriptions[language],
        drinks,
        language,
      },
    );
    return pairing;
  } catch (error) {
    console.error('Error in getDrinkPairing:', error);
    return 'Наш сомелье временно недоступен.';
  }
};

export const getIngredientInfo = async (
  dishName: string,
  language: Language,
): Promise<string | null> => {
  try {
    const { info } = await apiPost<{ info: string | null }>(
      '/api/ai/ingredients',
      { dishName, language },
    );
    return info ?? 'Информация временно недоступна.';
  } catch (error) {
    console.error('Error getting ingredient info:', error);
    return 'Информация временно недоступна.';
  }
};

export const findDishByImage = async (
  imageBase64: string,
  language: Language,
): Promise<number | null> => {
  try {
    const { id } = await apiPost<{ id: number | null }>(
      '/api/ai/visual-search',
      { imageBase64, language },
    );
    return typeof id === 'number' ? id : null;
  } catch (error) {
    console.error('Error in findDishByImage:', error);
    throw new Error('Не удалось выполнить визуальный поиск.');
  }
};
